import {
    Check,
    ChevronRight,
    Home,
    Info,
    MessageSquare,
    Phone,
    Search,
    Star,
    X
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { AnswerEditPanel } from '@/components/answers/answer-edit-panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

import { Answer, useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'


export default function KnowledgePage() {
    const { categoryId } = useParams<{ categoryId?: string }>()
    const { currentDevice } = useDevicesStore()

    const {
        answerCategories,
        currentCategory,
        currentAnswersByCategory,
        loadCategories,
        loadAnswersByCategory,
        setCurrentCategory,
        updateAnswer
    } = useCustomerStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [editingAnswer, setEditingAnswer] = useState<Answer | null>(null)
    const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
    const [isFavoritesView, setIsFavoritesView] = useState(false)
    const [showEditPanel, setShowEditPanel] = useState(false)

    // Get current answers - filters by visibleByCustomer for ANSWER type, supports favorites view
    const currentAnswers = useMemo(() => {
        if (isFavoritesView) {
            // For favorites view, collect all answers from all categories and filter favorites
            const allAnswers: Answer[] = []
            Object.values(currentAnswersByCategory).forEach(answers => {
                allAnswers.push(...answers)
            })
            return allAnswers.filter(answer => favoriteIds.has(answer.id))
        }

        if (!currentCategory) return []

        const answers = currentAnswersByCategory[currentCategory.id] || []

        // ANSWER type: only show visible answers
        if (currentCategory.type === 'ANSWER') {
            return answers.filter(answer => answer.visibleByCustomer !== false)
        }

        // OPTION type: show all
        return answers
    }, [currentCategory, currentAnswersByCategory, isFavoritesView, favoriteIds])

    // Filter answers by search query
    const filteredAnswers = useMemo(() => {
        if (!searchQuery.trim()) return currentAnswers

        const query = searchQuery.toLowerCase()
        return currentAnswers.filter(answer =>
            (answer.title || answer.displayTitle || '')?.toLowerCase().includes(query) ||
            answer.description?.toLowerCase().includes(query) ||
            answer.defaultAnswerActive?.toLowerCase().includes(query) ||
            answer.defaultAnswerInactive?.toLowerCase().includes(query) ||
            (answer.answerVariants && answer.answerVariants.some((v: any) =>
                v.content?.toLowerCase().includes(query)
            ))
        )
    }, [currentAnswers, searchQuery])

    // Sort answers: favorites first (starred), then by title
    const sortedAnswers = useMemo(() => {
        return [...filteredAnswers].sort((a, b) => {
            const aIsFav = favoriteIds.has(a.id)
            const bIsFav = favoriteIds.has(b.id)

            if (aIsFav && !bIsFav) return -1
            if (!aIsFav && bIsFav) return 1

            const aTitle = a.title || a.displayTitle || ''
            const bTitle = b.title || b.displayTitle || ''
            return aTitle.localeCompare(bTitle)
        })
    }, [filteredAnswers, favoriteIds])

    // Load categories on mount and when device changes
    useEffect(() => {
        if (currentDevice) {
            console.log('🔄 KnowledgePage: Loading categories for device:', currentDevice.deviceId)
            loadCategories()
        }
    }, [currentDevice?.deviceId])

    // Set current category from URL param
    useEffect(() => {
        if (categoryId === 'favorites') {
            setIsFavoritesView(true)
            // Load all categories to get all answers
            answerCategories
                .filter(cat => cat.name?.toUpperCase() !== 'DEFAULT')
                .forEach(cat => {
                    loadAnswersByCategory(cat.id)
                })
        } else if (categoryId && answerCategories.length > 0) {
            setIsFavoritesView(false)
            const category = answerCategories.find(c => c.id === categoryId)
            if (category) {
                setCurrentCategory(category)
            }
        }
    }, [categoryId, answerCategories, setCurrentCategory])

    // Load answers when category changes
    useEffect(() => {
        if (currentCategory) {
            loadAnswersByCategory(currentCategory.id)
        }
    }, [currentCategory, loadAnswersByCategory])

    // Debug: Log API data
    useEffect(() => {
        if (currentAnswers.length > 0) {
            console.log('🔍 DEBUG: Category:', currentCategory)
            console.log('🔍 DEBUG: Current Answers:', currentAnswers)
            console.log('🔍 DEBUG: First Answer Keys:', Object.keys(currentAnswers[0]))
            console.log('🔍 DEBUG: First Answer Full Object:', JSON.stringify(currentAnswers[0], null, 2))
        }
    }, [currentAnswers, currentCategory])

    const handleEditAnswer = (answer: Answer) => {
        console.log('✏️ EDIT: Opening answer for editing:', answer)
        console.log('✏️ EDIT: Answer type:', answer.answerVariants ? 'ANSWER' : 'OPTION')
        console.log('✏️ EDIT: Answer variants:', answer.answerVariants)
        console.log('✏️ EDIT: Full object:', JSON.stringify(answer, null, 2))
        setEditingAnswer(answer)
        setShowEditPanel(true)
    }

    const handleCloseEditPanel = () => {
        setShowEditPanel(false)
        setTimeout(() => setEditingAnswer(null), 300) // Wait for animation to finish
    }

    const toggleFavorite = (answerId: string) => {
        setFavoriteIds(prev => {
            const next = new Set(prev)
            if (next.has(answerId)) {
                next.delete(answerId)
            } else {
                next.add(answerId)
            }
            // Store favorites in localStorage
            localStorage.setItem('knowledge-favorites', JSON.stringify([...next]))
            return next
        })
    }

    // Load favorites from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('knowledge-favorites')
        if (stored) {
            try {
                const ids = JSON.parse(stored)
                setFavoriteIds(new Set(ids))
            } catch (e) {
                console.error('Failed to load favorites:', e)
            }
        }
    }, [])



    return (
        <TooltipProvider>
            <div className="flex flex-col h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-900">Knowledge</h1>
                                {(currentCategory || isFavoritesView) && (
                                    <p className="text-sm text-gray-600 mt-1">
                                        {isFavoritesView ? 'Favorites' : (currentCategory?.title || currentCategory?.name)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Breadcrumb Navigation */}
                    <div className="px-6 pb-3">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Link to="/customer/activity" className="hover:text-brand-primary flex items-center gap-1">
                                <Home className="h-4 w-4" />
                                <span>Home</span>
                            </Link>
                            <ChevronRight className="h-3 w-3" />
                            <Link to="/customer/knowledge" className="hover:text-brand-primary">
                                Knowledge
                            </Link>
                            {isFavoritesView ? (
                                <>
                                    <ChevronRight className="h-3 w-3" />
                                    <span className="text-gray-900">Favorites</span>
                                </>
                            ) : currentCategory && (
                                <>
                                    <ChevronRight className="h-3 w-3" />
                                    <span className="text-gray-900">
                                        {currentCategory.title || currentCategory.name}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="bg-white border-b px-6 py-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by name or keyword"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 bg-white"
                        />
                    </div>
                </div>

                {/* Main Content - Table + Edit Panel */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Table Container */}
                    <div className={`overflow-auto bg-white m-6 rounded-lg border border-gray-200 shadow-sm transition-all duration-300 ${showEditPanel ? 'w-[350px] mr-3' : 'flex-1'}`}>
                        {!currentCategory && !isFavoritesView ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <p className="text-gray-600 font-medium">Keine Kategorie ausgewählt</p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Wähle eine Kategorie aus der Sidebar aus
                                    </p>
                                </div>
                            </div>
                        ) : sortedAnswers.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <p className="text-gray-600 font-medium">
                                        {isFavoritesView
                                            ? 'Keine Favoriten'
                                            : searchQuery
                                                ? 'Keine Antworten gefunden'
                                                : 'Noch keine Antworten'
                                        }
                                    </p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        {isFavoritesView
                                            ? 'Klicke auf den Stern, um Antworten als Favorit zu markieren'
                                            : searchQuery
                                                ? `Keine Antworten entsprechen "${searchQuery}"`
                                                : 'Erstelle deine erste Antwort'
                                        }
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b">
                                        <TableHead className={showEditPanel ? "w-[200px] h-12" : "w-[400px] h-12"}>
                                            <span className="text-sm font-semibold text-gray-700">Title</span>
                                        </TableHead>
                                        {!showEditPanel && (
                                            <>
                                                <TableHead className="h-12">
                                                    <span className="text-sm font-semibold text-gray-700">
                                                        {currentCategory?.type === 'ANSWER' ? 'Answer Variants' : 'Current answer'}
                                                    </span>
                                                </TableHead>
                                                <TableHead className="w-[80px] h-12"></TableHead>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedAnswers.map((answer) => {
                                        // Determine if it's ANSWER or OPTION type
                                        const isAnswerType = !!answer.answerVariants
                                        const displayName = answer.displayTitle || answer.title || 'Unbenannte Antwort'

                                        // Get current answer text or variants list
                                        let answerContent: React.ReactNode = '-'
                                        if (isAnswerType && answer.answerVariants && answer.answerVariants.length > 0) {
                                            // Show list of answer variants for ANSWER type
                                            answerContent = (
                                                <div className="flex flex-col gap-1">
                                                    {answer.answerVariants.map((variant: any, idx: number) => (
                                                        <div key={variant.id || idx} className="text-sm text-gray-600 border-b border-gray-100 last:border-0 py-1">
                                                            <span className="text-xs text-gray-500 mr-2">
                                                                {variant.defaultAnswer ? '(default)' : '(template)'}
                                                            </span>
                                                            <span className="truncate">{variant.content}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        } else if (!isAnswerType) {
                                            // OPTION type - show active or inactive answer
                                            const currentText = answer.enabled
                                                ? (answer.customerAnswerActive || answer.defaultAnswerActive)
                                                : (answer.customerAnswerInactive || answer.defaultAnswerInactive)
                                            answerContent = (
                                                <div className="text-sm text-gray-500 max-w-2xl truncate">
                                                    {currentText || '-'}
                                                </div>
                                            )
                                        }

                                        return (
                                            <TableRow
                                                key={answer.id}
                                                className={`hover:bg-gray-50/50 border-b cursor-pointer ${editingAnswer?.id === answer.id && showEditPanel ? 'bg-brand-primary/5' : ''}`}
                                                onClick={() => showEditPanel ? handleEditAnswer(answer) : null}
                                            >
                                                <TableCell className="py-4">
                                                    <div className="flex items-center gap-3">
                                                        {/* Favorite star */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toggleFavorite(answer.id)
                                                            }}
                                                            className="text-gray-400 hover:text-yellow-500 transition-colors flex-shrink-0"
                                                        >
                                                            <Star
                                                                className={`h-5 w-5 ${favoriteIds.has(answer.id) ? 'fill-yellow-500 text-yellow-500' : ''}`}
                                                            />
                                                        </button>

                                                        <div className="flex items-center gap-2">
                                                            {/* Description tooltip */}
                                                            {answer.description && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <Info className="h-4 w-4 text-blue-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent className="max-w-xs">
                                                                        <p>{answer.description}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}

                                                            {/* Forwarding indicator */}
                                                            {answer.forwardingEnabled && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <Phone className="h-4 w-4 text-green-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Forwarding active</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}

                                                            {/* SMS indicator */}
                                                            {answer.smsEnabled && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <MessageSquare className="h-4 w-4 text-blue-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>SMS active</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </div>

                                                        {/* Title */}
                                                        <span className="font-medium text-gray-900">
                                                            {displayName}
                                                        </span>

                                                        {/* Status indicator for OPTION type */}
                                                        {!isAnswerType && (
                                                            <div className="ml-2">
                                                                {answer.enabled ? (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                                <Check className="h-4 w-4 text-green-600" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Enabled</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                                <X className="h-4 w-4 text-red-500" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Disabled</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                {!showEditPanel && (
                                                    <>
                                                        <TableCell className="py-4">
                                                            {answerContent}
                                                        </TableCell>
                                                        <TableCell className="py-4">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleEditAnswer(answer)
                                                                }}
                                                                className="text-gray-600 hover:text-brand-primary hover:bg-gray-100"
                                                            >
                                                                Manage
                                                            </Button>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    {/* Edit Panel - Slide in from right */}
                    <div
                        className={`bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${showEditPanel ? 'flex-1 mr-6 my-6 rounded-lg' : 'w-0 mr-0'
                            } overflow-hidden`}
                    >
                        {editingAnswer && showEditPanel && (
                            <AnswerEditPanel
                                answer={editingAnswer}
                                onClose={handleCloseEditPanel}
                                onSave={async (updatedAnswer, customerText, useSystemDefault) => {
                                    await updateAnswer(updatedAnswer, customerText, useSystemDefault)
                                    if (currentCategory) {
                                        await loadAnswersByCategory(currentCategory.id)
                                    }
                                    handleCloseEditPanel()
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    )
}
